import type { ExportColumn } from '@/hooks/useCrmExport';

import type { ColumnConfig } from '../../core/types';
import { getLookupColumnParts } from './lookupColumn';

/**
 * Map visible columns to the export `columns` payload contract:
 * - `property` columns -> the property name (string)
 * - `lookup` columns -> { association, target_property }
 * `object_name` and `association` (name-chip) kinds are NOT representable in
 * the export contract (own-property + lookup only), so they are omitted.
 *
 * NOTE ON EMPTY RESULTS: an empty return is ambiguous on its own — it can mean
 * "there were no columns" OR "every visible column was a non-representable kind
 * (only object_name / association name-chips visible)". The caller MUST NOT
 * treat an empty result from a non-empty visible-column set as "no selection"
 * and fall back to the default full-property export — that would download every
 * property instead of matching the narrowed view. Callers distinguish the two
 * cases by also inspecting whether any visible columns existed (see
 * `crm-object-list-page.tsx` handleExport). Because the only exportable entity
 * types (contact/company/deal) carry an internal-read-gated `ahoy_display_name`,
 * `object_name` cannot be mapped to a representable property here.
 */
export function buildExportColumns(columns: ColumnConfig[]): ExportColumn[] {
  const result: ExportColumn[] = [];
  for (const col of columns) {
    if (col.kind === 'property' && col.name) {
      result.push(col.name);
    } else if (col.kind === 'lookup') {
      const parts = getLookupColumnParts(col);
      if (parts) {
        result.push({
          association: parts.associationTypeName,
          target_property: parts.targetPropertyName,
        });
      }
    }
  }
  return result;
}
